import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Item, Customer } from '../types';
import { api } from '../api';
import { printReceipt, PrintMode } from '../utils/print';
import { authStorage } from '../auth';

type PurchaseRow = {
  id: string;
  purchaseId: string;
  customerId: string;
  name: string;
  phone: string;
  address: string;
  cccd: string;
  purchaseNumber?: string;
  customerType: string;
  items: Item[];
  totalAmount: number;
  date: string;
  createdAt: string;
};

interface PurchaseListPageProps {
  selectedDate: string;
  onAddCustomer: () => void;
  onViewCustomer: (purchase: PurchaseRow) => void;
  onEditCustomerInfo: (purchase: PurchaseRow) => void;
  setErrorMessage?: (msg: string) => void;
}
export interface PurchaseListPageRef {
  reloadData: (date: string) => Promise<void>;
}

export const PurchaseListPage = React.forwardRef<PurchaseListPageRef, PurchaseListPageProps>(({
  selectedDate,
  onAddCustomer,
  onViewCustomer,
  onEditCustomerInfo,
  setErrorMessage
}, ref) => {
  const [purchases, setPurchases] = React.useState<PurchaseRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [sortField, setSortField] = React.useState<'name' | 'phone' | 'address' | 'items' | 'total' | 'time' | 'purchaseNumber'>('time');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [printMode, setPrintMode] = React.useState<PrintMode>(() => {
    return (localStorage.getItem('printMode') as PrintMode) || 'double-a5';
  });
  const [changeCustomerDialog, setChangeCustomerDialog] = React.useState<{
    open: boolean;
    purchase: PurchaseRow | null;
  }>({ open: false, purchase: null });
  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = React.useState<Customer | null>(null);
  const [changingCustomer, setChangingCustomer] = React.useState(false);

  React.useEffect(() => {
    setIsAdmin(authStorage.isAdmin());
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await api.getCustomers();
      setCustomers(data);
    } catch (err) {
      console.error('Lỗi tải danh sách khách hàng', err);
    }
  };

  const loadData = React.useCallback(async (date: string) => {
    setLoading(true);
    setErrorMessage?.('');
    try {
      const purchasesData = await api.getPurchases(date);

      const todayPurchases = purchasesData.map<PurchaseRow>((p) => ({
        id: p.id,
        purchaseId: p.id,
        customerId: p.customerId,
        name: p.customer?.name ?? '',
        phone: p.customer?.phone ?? '',
        address: p.customer?.address ?? '',
        cccd: p.customer?.cccd ?? '',
          purchaseNumber: (p as any).purchaseNumber ?? undefined,
        customerType: p.customer?.customerType ?? 'Retail',
        items: p.items ?? [],
        totalAmount: p.totalAmount,
        date: p.date,
        createdAt: p.createdAt,
      }));

      setPurchases(todayPurchases);
    } catch (err) {
      console.error('Lỗi tải dữ liệu', err);
      setErrorMessage?.('Lỗi tải dữ liệu phiếu mua');
    } finally {
      setLoading(false);
    }
  }, [setErrorMessage]);

  React.useEffect(() => {
    loadData(selectedDate);
  }, [selectedDate, loadData]);

  React.useImperativeHandle(ref, () => ({
    reloadData: loadData,
  }), [loadData]);

  const handleSort = (field: 'name' | 'phone' | 'address' | 'items' | 'total' | 'time' | 'purchaseNumber') => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'time' ? 'desc' : 'asc');
    }
  };

  const handlePrintReceipt = (purchase: PurchaseRow) => {
    printReceipt({
      customerName: purchase.name,
      customerPhone: purchase.phone,
      customerAddress: purchase.address,
      items: purchase.items,
      totalAmount: purchase.totalAmount,
      date: purchase.date,
      purchaseNumber: purchase.purchaseNumber,
      printMode: printMode,
    });
  };

  const handleDeletePurchase = async (purchase: PurchaseRow) => {
    if (!isAdmin) return;
    const ok = window.confirm('Bạn có chắc muốn xóa phiếu này?');
    if (!ok) return;
    try {
      await api.deletePurchase(purchase.purchaseId);
      setPurchases(prev => prev.filter(p => p.purchaseId !== purchase.purchaseId));
    } catch (err: any) {
      console.error('Không thể xóa phiếu', err.message);
    }
  };

  const handleOpenChangeCustomer = (purchase: PurchaseRow) => {
    setChangeCustomerDialog({ open: true, purchase });
    setSelectedCustomer(null);
  };

  const handleCloseChangeCustomer = () => {
    setChangeCustomerDialog({ open: false, purchase: null });
    setSelectedCustomer(null);
  };

  const handleConfirmChangeCustomer = async () => {
    if (!changeCustomerDialog.purchase || !selectedCustomer) return;
    
    setChangingCustomer(true);
    try {
      await api.changePurchaseCustomer(changeCustomerDialog.purchase.purchaseId, selectedCustomer.id);
      // Reload data to get updated purchase
      await loadData(selectedDate);
      handleCloseChangeCustomer();
    } catch (err: any) {
      console.error('Lỗi đổi khách hàng', err);
      alert('Lỗi đổi khách hàng: ' + err.message);
    } finally {
      setChangingCustomer(false);
    }
  };

  const sortedPurchases = React.useMemo(() => {
    const copy = [...purchases];
    copy.sort((a, b) => {
      let result = 0;
      switch (sortField) {
        case 'name':
          result = a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' });
          break;
        case 'phone':
          result = a.phone.localeCompare(b.phone);
          break;
        case 'address':
          result = a.address.localeCompare(b.address, 'vi', { sensitivity: 'base' });
          break;
        case 'items':
          result = a.items.length - b.items.length;
          break;
        case 'total':
          result = a.totalAmount - b.totalAmount;
          break;
        case 'time':
        default:
          result = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }
      return sortDirection === 'asc' ? result : -result;
    });
    return copy;
  }, [purchases, sortDirection, sortField]);

  return (
    <Paper elevation={3} sx={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 150px)' }}>
      <Box sx={{ p: 1, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Typography >
            📋 DS Phiếu Mua
          </Typography>
          <Box>
            <Typography >
              🧾 {purchases.length} phiếu
            </Typography>
          </Box>
          <Box>
            <Typography color="warning.main">
              💰 {purchases.reduce((sum, p) => sum + p.totalAmount, 0).toLocaleString('vi-VN')} đ
            </Typography>
          </Box>
          <Box>
            <Typography>
              🗐 Mẫu phiếu:
              <Select size="small"
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={printMode}
                label="Mẫu phiếu"
                sx={{ color: 'orange', width: '150px', ml: 1 }}
                onChange={(value) => {
                  setPrintMode(value.target.value as PrintMode); 
                  localStorage.setItem('printMode', value.target.value as PrintMode); 
                }}>
                <MenuItem value="single-a6">1 trang</MenuItem >
                <MenuItem value="double-a5">2 trang ghép</MenuItem >
                <MenuItem value="double-a6-separate">2 trang riêng</MenuItem>
              </Select>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {loading && <CircularProgress size={20} color="inherit" />}
          <IconButton
            onClick={onAddCustomer}
            sx={{ color: 'white' }}
            title="Thêm phiếu"
          >
            <AddIcon />
          </IconButton>
          <IconButton
            onClick={() => loadData(selectedDate)}
            sx={{ color: 'white' }}
            title="Làm mới"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>



      {purchases.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Chưa có phiếu mua nào
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Nhấn nút "Thêm Phiếu" để bắt đầu
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAddCustomer}
          >
            Thêm Phiếu Đầu Tiên
          </Button>
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell 
                  align="center" 
                  sx={{ fontWeight: 'bold', width: '50px',  maxWidth: '80px' }}
                >
                    STT
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none', width: '120px',  maxWidth: '150px'  }}
                  onClick={() => handleSort('purchaseNumber')}
                >
                  Số Phiếu {sortField === 'purchaseNumber' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none', maxWidth: '280px',  width: '280px' }}
                  onClick={() => handleSort('name')}
                >
                  Tên Khách Hàng {sortField === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none', width: '150px',  maxWidth: '150px'}}
                  onClick={() => handleSort('phone')}
                >
                  Số Điện Thoại {sortField === 'phone' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('address')}
                >
                  Địa Chỉ {sortField === 'address' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none', width: '100px',  maxWidth: '100px' }}
                  onClick={() => handleSort('items')}
                >
                  Số Loại {sortField === 'items' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none', width: '150px',  maxWidth: '150px' }}
                  onClick={() => handleSort('total')}
                >
                  Tổng Tiền {sortField === 'total' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none', width: '150px',  maxWidth: '150px' }}
                  onClick={() => handleSort('time')}
                >
                  Thời Gian {sortField === 'time' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '150px',  maxWidth: '150px' }}>Thao Tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedPurchases.map((purchase, index) => (
                <TableRow
                  key={purchase.id}
                  sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                >
                  <TableCell align="center" >{index + 1}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {purchase.purchaseNumber ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {purchase.customerType === 'Wholesale' ? '👨🏻‍💼' : '🙍‍♂️'}
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {purchase.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      📞 {purchase.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      🏠 {purchase.address}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${purchase.items.length} loại`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {purchase.totalAmount.toLocaleString('vi-VN')} đ
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {new Date(purchase.date).toLocaleTimeString('vi-VN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <IconButton
                        color="primary"
                        onClick={() => onViewCustomer(purchase)}
                        size="small"
                        title="Xem chi tiết"
                      >
                        👁️
                      </IconButton>
                      <IconButton
                        color="info"
                        onClick={() => handleOpenChangeCustomer(purchase)}
                        size="small"
                        title="Đổi khách hàng"
                      >
                        👨‍👨
                      </IconButton>
                      <IconButton
                        color="success"
                        onClick={() => handlePrintReceipt(purchase)}
                        size="small"
                        title="In phiếu"
                      >
                        🖨️
                      </IconButton>
                      {isAdmin && (
                        <IconButton
                          color="error"
                          onClick={() => handleDeletePurchase(purchase)}
                          size="small"
                          title="Xóa phiếu (Admin)"
                        >
                          ❌
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Đổi Khách Hàng */}
      <Dialog 
        open={changeCustomerDialog.open} 
        onClose={handleCloseChangeCustomer}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Đổi Khách Hàng
        </DialogTitle>
        <DialogContent>
          {changeCustomerDialog.purchase && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Khách hàng hiện tại:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                {changeCustomerDialog.purchase.name} - {changeCustomerDialog.purchase.phone}
              </Typography>
              
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => `${option.name} - ${option.phone}`}
                value={selectedCustomer}
                onChange={(_, newValue) => setSelectedCustomer(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Chọn khách hàng mới"
                    placeholder="Tìm kiếm theo tên hoặc số điện thoại"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.phone} - {option.address}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeCustomer} disabled={changingCustomer}>
            Hủy
          </Button>
          <Button 
            onClick={handleConfirmChangeCustomer} 
            variant="contained"
            disabled={!selectedCustomer || changingCustomer}
          >
            {changingCustomer ? 'Đang xử lý...' : 'Xác nhận'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
});

PurchaseListPage.displayName = 'PurchaseListPage';
